import { type Map as LeafletMap } from 'leaflet';
import { type MilSym } from './renderer';
import { type TacticalSymbol } from './TacticalSymbol';
import { AttackLine } from './AttackLine';
import { SIDC_AXIS_OF_ADVANCE, sidcFamily } from './sidc';

/**
 * SIDC → concrete symbol constructor.
 *
 * The APP-6D symbol id is the single discriminator for a tactical graphic: it is
 * what the user picks, what the backend stores, and what the CoT payload carries.
 * Keying the factory off it means adding a symbol family is one entry here plus
 * one `TacticalSymbol` subclass — no local `type` nicknames to keep in sync
 * across the web client, the backend and Android.
 *
 * Lookups are keyed on the *family* code (see {@link sidcFamily}): affiliation
 * and status are properties of a particular drawn graphic, not of the symbol
 * family, so an enemy planned arrow resolves to the same constructor as a
 * friendly one and each family stays a single entry.
 */
type SymbolConstructor = new (
    map: LeafletMap,
    milSym: MilSym,
    sidc: string,
) => TacticalSymbol;

const REGISTRY: Record<string, SymbolConstructor> = {
    [SIDC_AXIS_OF_ADVANCE]: AttackLine,
};

/** Every symbol family this client knows how to draw and render. */
export function supportedSidcs(): string[] {
    return Object.keys(REGISTRY);
}

export function isSupportedSidc(sidc: string): boolean {
    return sidcFamily(sidc) in REGISTRY;
}

/**
 * Construct the symbol for `sidc`, or null when this client doesn't know it —
 * a graphic drawn by a newer client must be skipped, not crash the map.
 *
 * The full code is handed to the constructor, not the family key it was looked
 * up by: the symbol renders and colours itself from its own affiliation.
 */
export function createSymbol(
    sidc: string,
    map: LeafletMap,
    milSym: MilSym,
): TacticalSymbol | null {
    const Ctor = REGISTRY[sidcFamily(sidc)];
    return Ctor ? new Ctor(map, milSym, sidc) : null;
}
